import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { ChatComposer } from "@/components/chat/ChatComposer"
import { queueAssistantChatOperation } from "@/lib/assistant-chat-operations"
import { useAssistantStore } from "@/stores/assistant-store"

/** Own stash key, so a SharePoint-connect round trip restores the draft here, not on the Assistant page. */
const HOME_DRAFT_STASH_KEY = "chat-draft-stash:home"

const START_FAILED = "Couldn't start a chat. Try again in a moment."

type ChatAttachmentRef = {
  attachmentId: string
  s3Key: string
  filename: string
  mimeType: string
  size: number
}

// Home's prompt box: the message starts a NEW Assistant chat and opens the
// Assistant page on it. The turn is dispatched before navigating, so the page
// arrives mid-stream (AssistantPage skips its mount reload while streaming).
// No scope control here. Not rendered for viewers: the chat session it needs
// is refused to them (HomePage gates it).
export function HomePrompt() {
  const navigate = useNavigate()
  const [isStarting, setIsStarting] = useState(false)
  const ownerRef = useRef<{ current: boolean; busy: boolean } | null>(null)
  useEffect(() => {
    const owner = { current: true, busy: false }
    ownerRef.current = owner
    return () => {
      owner.current = false
      if (owner.busy) useAssistantStore.setState({ isLoading: false })
    }
  }, [])

  const handleAsk = async (content: string, attachments?: ChatAttachmentRef[]): Promise<boolean> => {
    const store = useAssistantStore.getState
    const owner = ownerRef.current
    const isCurrent = () => owner?.current === true
    setIsStarting(true)
    try {
      return await queueAssistantChatOperation(async () => {
        if (!isCurrent() || !owner) return false
        owner.busy = true
        if (!store().sessionId) await store().reloadActiveThread(isCurrent)
        if (!isCurrent()) return false
        if (!store().sessionId) {
          toast.error(START_FAILED)
          return false
        }
        await store().startNewChat(isCurrent)
        if (!isCurrent()) return false
        const { activeThreadId, error } = store()
        if (error || !activeThreadId) {
          toast.error(START_FAILED)
          return false
        }
        void store().sendMessage(content, attachments)
        void navigate(`/app/assistant/${encodeURIComponent(activeThreadId)}`)
        return true
      })
    } finally {
      if (owner) owner.busy = false
      if (isCurrent()) setIsStarting(false)
    }
  }

  return (
    // ChatComposer pads itself (px-4 pt-3 pb-6). The negative margins cancel
    // that padding, and `fullWidth` drops its centred max-w-3xl, so the box
    // spans Home's content column edge to edge, like the cards below it.
    <div className="-mx-4 -mt-3 -mb-6" data-testid="home-prompt">
      <ChatComposer
        sendMessage={handleAsk}
        cancelStream={() => {}}
        isLoading={isStarting}
        isStreaming={false}
        label="Ask the AI assistant"
        placeholder="Ask about your bids, or tell me what to do…"
        draftStashKey={HOME_DRAFT_STASH_KEY}
        fullWidth
      />
    </div>
  )
}
